import React from "react";
import { useSimpleTableContext } from "../../contexts/SimpleTableContext";
import { getFieldValue } from "./tableCellFieldValue";
import { getCellRenderer } from "./TableCellRouter";
import { TableCellWrapper } from "./TableCellWrapper";
import type { BaseEntity, UniversalInlineEditHandler, EntityWithRelations } from "../../types";
import type { TableCellProps } from "./TableCell.types";

export function TableCell<TData extends BaseEntity>({
  fieldId,
  fieldConfig,
  item,
  cellIndex,
  totalCells,
  isTextareaField,
  dataRowIds,
  dataIndex,
}: TableCellProps<TData>) {
  // Get context for all table state and handlers
  const context = useSimpleTableContext<TData>();

  const {
    config,
    state,
    callbacks,
    auth,
    hooks,
    utils,
    onEdit,
    onView,
    onDelete,
    onInlineEdit,
    onOpenDetailModal,
    data,
  } = context;

  const fieldValue = getFieldValue(item as EntityWithRelations<TData>, fieldId);
  // Only primitive values can seed a simple "Filter by this value" from the
  // row context menu; objects/dates are left out (undefined attr).
  const primitiveFilterValue =
    typeof fieldValue === "string" ||
    typeof fieldValue === "number" ||
    typeof fieldValue === "boolean"
      ? String(fieldValue)
      : undefined;
  const cellLabel =
    typeof fieldConfig.label === "string" ? fieldConfig.label : String(fieldConfig.label);
  const isEditing = state.editingCell?.rowId === item.id && state.editingCell?.fieldId === fieldId;
  const inlineEditHandler = (onInlineEdit as UniversalInlineEditHandler) || (async () => {});

  // Get the appropriate cell renderer based on field type
  const cellContent = getCellRenderer({
    fieldId,
    fieldValue,
    fieldConfig,
    item,
    isEditing,
    isTextareaField,
    config,
    state,
    callbacks,
    auth,
    data,
    inlineEditHandler,
    onOpenDetailModal,
    dataRowIds,
    dataIndex,
    hooks,
    onEdit,
    onView,
    onDelete,
  });

  return (
    <TableCellWrapper
      fieldId={fieldId}
      fieldConfig={fieldConfig}
      cellIndex={cellIndex}
      totalCells={totalCells}
      isTextareaField={isTextareaField}
      columnStyle={utils.getColumnStyleCallback(fieldId, fieldConfig)}
      fieldLabel={cellLabel}
      filterValue={primitiveFilterValue}
    >
      {cellContent}
    </TableCellWrapper>
  );
}

// Memoized wrapper for performance
export const MemoizedTableCell = React.memo(TableCell) as typeof TableCell;
